import { useState } from 'react';
import { Mail } from 'lucide-react';
import { useDialogA11y } from '../lib/useDialogA11y';
import './MarketingConsentPrompt.css';

interface MarketingConsentPromptProps {
  isOpen: boolean;
  onAnswer: (consent: boolean) => Promise<void>;
}

export default function MarketingConsentPrompt({ isOpen, onAnswer }: MarketingConsentPromptProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Spørgsmålet stilles én gang og skal besvares aktivt: Escape lukker det ikke.
  const { ref: dialogRef } = useDialogA11y({ open: isOpen });

  if (!isOpen) return null;

  const answer = async (consent: boolean) => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onAnswer(consent);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Dit valg kunne ikke gemmes. Prøv igen.');
      setSubmitting(false);
    }
  };

  return (
    <div
      ref={dialogRef}
      className="mcp-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="marketing-consent-title"
    >
      <div className="mcp-card">
        <span className="mcp-icon" aria-hidden="true">
          <Mail size={20} />
        </span>
        <h2 id="marketing-consent-title" className="mcp-title">
          Vil du have gode råd fra Plotwise på mail?
        </h2>
        <p className="mcp-text">
          Det er maksimalt 1–2 mails om ugen. Fx en venlig påmindelse, hvis du har tegnet en mark
          uden at vælge en frøblanding, eller gode tips til sæsonen.
        </p>
        {error && (
          <p role="alert" className="mcp-error">
            {error}
          </p>
        )}
        <div className="mcp-actions">
          <button
            type="button"
            className="mcp-btn mcp-btn-no"
            disabled={submitting}
            onClick={() => void answer(false)}
          >
            Nej tak
          </button>
          <button
            type="button"
            className="mcp-btn mcp-btn-yes"
            disabled={submitting}
            onClick={() => void answer(true)}
          >
            Ja tak
          </button>
        </div>
        <p className="mcp-note">Du kan altid ændre det under Din profil.</p>
      </div>
    </div>
  );
}
